import React, { useCallback, useEffect, useRef, useState } from 'react';
import galleryImages from '../galaries';
import './AboutSection.css';

const AUTOPLAY_DELAY = 3600;
const TRANSITION_DURATION = 850;

const AboutSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [counters, setCounters] = useState({ years: 0, states: 0, personnel: 0, clients: 0 });
  const [showGalleryModal, setShowGalleryModal] = useState(false);
  const [showStoryModal, setShowStoryModal] = useState(false);
  const [galleryImagesList, setGalleryImagesList] = useState([]);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [previousSlide, setPreviousSlide] = useState(0);
  const [direction, setDirection] = useState(1);
  const [hoverPaused, setHoverPaused] = useState(false);
  const [isHidden, setIsHidden] = useState(false);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [isImageLoaded, setIsImageLoaded] = useState(false);
  const [touchStartX, setTouchStartX] = useState(null);
  const sectionRef = useRef(null);
  const galleryFrameRef = useRef(null);
  const galleryPreloadedImagesRef = useRef({});
  const autoplayTimerRef = useRef(null);

  // Intersection Observer for animations
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          animateCounters();
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  // Counter animation
  const animateCounters = () => {
    const targets = { years: 15, states: 16, personnel: 120, clients: 1762 };
    const duration = 2000;
    const steps = 60;
    const interval = duration / steps;

    let step = 0;
    const timer = setInterval(() => {
      step++;
      const progress = step / steps;
      const easeOut = 1 - Math.pow(1 - progress, 3);

      setCounters({
        years: Math.round(targets.years * easeOut),
        states: Math.round(targets.states * easeOut),
        personnel: Math.round(targets.personnel * easeOut),
        clients: Math.round(targets.clients * easeOut)
      });

      if (step >= steps) clearInterval(timer);
    }, interval);
  };

  useEffect(() => {
    setGalleryImagesList(galleryImages);
  }, []);

  useEffect(() => {
    if (!showGalleryModal || !galleryImagesList.length) return undefined;

    const indexesToPreload = [
      currentSlide,
      (currentSlide + 1) % galleryImagesList.length,
      (currentSlide - 1 + galleryImagesList.length) % galleryImagesList.length
    ];

    indexesToPreload.forEach((index) => {
      const image = galleryImagesList[index];

      if (!image || galleryPreloadedImagesRef.current[image.id]) return;

      const imageElement = new Image();
      imageElement.src = image.src;
      galleryPreloadedImagesRef.current[image.id] = imageElement;
    });

    return () => {
      Object.values(galleryPreloadedImagesRef.current).forEach((image) => {
        image.onload = null;
        image.onerror = null;
      });
      galleryPreloadedImagesRef.current = {};
    };
  }, [showGalleryModal, currentSlide, galleryImagesList]);

  useEffect(() => {
    const handleVisibilityChange = () => setIsHidden(document.hidden);

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    const updateMotionPreference = () => setPrefersReducedMotion(mediaQuery.matches);

    updateMotionPreference();

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', updateMotionPreference);
      return () => mediaQuery.removeEventListener('change', updateMotionPreference);
    }

    mediaQuery.addListener(updateMotionPreference);
    return () => mediaQuery.removeListener(updateMotionPreference);
  }, []);

  useEffect(() => {
    if (!showGalleryModal) return undefined;

    const timer = window.setTimeout(() => {
      galleryFrameRef.current?.focus();
    }, 0);

    return () => window.clearTimeout(timer);
  }, [showGalleryModal]);

  const goToSlide = useCallback((index) => {
    if (!galleryImagesList.length || isTransitioning) return;

    const total = galleryImagesList.length;
    const nextSlide = (index + total) % total;

    if (nextSlide === currentSlide) return;

    setPreviousSlide(currentSlide);
    setCurrentSlide(nextSlide);
    setDirection(nextSlide > currentSlide ? 1 : -1);
    setIsTransitioning(true);
    setIsImageLoaded(false);
  }, [currentSlide, galleryImagesList.length, isTransitioning]);

  const goNext = useCallback(() => {
    goToSlide(currentSlide + 1);
  }, [currentSlide, goToSlide]);

  const goPrevious = useCallback(() => {
    goToSlide(currentSlide - 1);
  }, [currentSlide, goToSlide]);

  const closeGalleryModal = useCallback(() => {
    setShowGalleryModal(false);
    setHoverPaused(false);
  }, []);

  const handleGalleryKeyDown = (event) => {
    if (event.target !== event.currentTarget) return;

    if (event.key === 'ArrowRight') {
      event.preventDefault();
      goNext();
    }

    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      goPrevious();
    }

    if (event.key === 'Escape') {
      event.preventDefault();
      closeGalleryModal();
    }
  };

  const handleTouchStart = (event) => {
    if (event.touches?.length) {
      setTouchStartX(event.touches[0].clientX);
    }
  };

  const handleTouchEnd = (event) => {
    if (touchStartX === null || !event.changedTouches?.length) return;

    const touchEndX = event.changedTouches[0].clientX;
    const swipeDistance = touchEndX - touchStartX;

    if (Math.abs(swipeDistance) > 50) {
      swipeDistance < 0 ? goNext() : goPrevious();
    }

    setTouchStartX(null);
  };

  const autoplayPaused = hoverPaused || isHidden || !showGalleryModal || prefersReducedMotion;
  const activeImage = galleryImagesList[currentSlide];
  const previousImage = galleryImagesList[previousSlide];
  const totalSlides = galleryImagesList.length;

  useEffect(() => {
    setIsImageLoaded(false);
  }, [activeImage?.src]);

  useEffect(() => {
    if (!isTransitioning) return undefined;

    const timer = window.setTimeout(() => setIsTransitioning(false), TRANSITION_DURATION);

    return () => window.clearTimeout(timer);
  }, [isTransitioning]);

  useEffect(() => {
    if (autoplayPaused || totalSlides < 2) return undefined;

    autoplayTimerRef.current = window.setTimeout(goNext, AUTOPLAY_DELAY);

    return () => {
      if (autoplayTimerRef.current) {
        window.clearTimeout(autoplayTimerRef.current);
        autoplayTimerRef.current = null;
      }
    };
  }, [autoplayPaused, goNext, totalSlides]);

  const stats = [
    { value: counters.years, suffix: '+', label: 'Years Experience', icon: 'fa-calendar-alt' },
    { value: counters.states, suffix: '', label: 'States Covered', icon: 'fa-map-marked-alt' },
    { value: counters.personnel, suffix: '+', label: 'Security Personnel', icon: 'fa-users' },
    { value: counters.clients, suffix: '+', label: 'Happy Clients', icon: 'fa-handshake' }
  ];

  const features = [
    {
      icon: 'fa-shield-alt',
      title: 'Trusted Protection',
      desc: 'We place people at the heart of everything we do, emphasizing trust, respect, and personal growth.'
    },
    {
      icon: 'fa-award',
      title: 'Woman-Owned Business',
      desc: 'Proudly woman-owned and led, bringing a personal commitment to excellence that sets us apart.'
    },
    {
      icon: 'fa-network-wired',
      title: 'National Presence',
      desc: 'Combining the reliability of a nationwide provider with the responsiveness of local expertise.'
    },
    {
      icon: 'fa-headset',
      title: '24/7 Operations',
      desc: 'Round-the-clock support and monitoring to ensure your security needs are always met.'
    }
  ];

  const industries = [
    'Residential Security', 'Retail Security', 'Industrial Safety', 
    'Commercial Security', 'Campus Safety', 'Government Facilities'
  ];

  return (
    <section 
      ref={sectionRef}
      className={`about-section-futuristic ${isVisible ? 'visible' : ''}`} 
      id="about"
    >
      {/* Gallery Modal */}
      {showGalleryModal && (
        <div className="about-modal-overlay gallery-overlay" onClick={closeGalleryModal}>
          <div className="about-modal gallery-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-body gallery-modal-body">
              {totalSlides > 0 && activeImage ? (
                <div
                  ref={galleryFrameRef}
                  tabIndex={0}
                  role="region"
                  aria-label="Kamelot story gallery slideshow"
                  aria-live="polite"
                  className={`story-gallery-frame cyber-security-gallery ${isTransitioning ? 'is-transitioning' : ''} ${autoplayPaused ? 'is-paused' : 'is-active'}`}
                  onMouseEnter={() => setHoverPaused(true)}
                  onMouseLeave={() => setHoverPaused(false)}
                  onKeyDown={handleGalleryKeyDown}
                  onTouchStart={handleTouchStart}
                  onTouchEnd={handleTouchEnd}
                >
                  <button className="gallery-close-button" type="button" onClick={closeGalleryModal} aria-label="Close gallery">
                    <i className="fas fa-times"></i>
                  </button>

                  <div className="gallery-stage">
                    {previousImage && previousSlide !== currentSlide && (
                      <div className={`gallery-slide gallery-slide-out gallery-direction-${direction}`}>
                        <img src={previousImage.src} alt={previousImage.name} />
                      </div>
                    )}
                    <div className={`gallery-slide gallery-slide-in gallery-direction-${direction}`}>
                      <img
                        src={activeImage.src}
                        alt={activeImage.name}
                        onLoad={() => setIsImageLoaded(true)}
                      />
                      {!isImageLoaded && <div className="gallery-loader">SECURE LOAD</div>}
                    </div>

                    <div className="gallery-scanline"></div>
                    <div className="gallery-corner-lock">
                      <i className="fas fa-lock"></i>
                      <span>KAMELOT SECURE ARCHIVE</span>
                    </div>
                    <div className="gallery-hud">
                      <div className="gallery-hud-left">
                        <span className="gallery-hud-label">SECURE STORY FEED</span>
                        <span className="gallery-hud-sublabel">{autoplayPaused ? 'AUTOPLAY HOLD' : 'AUTOPLAY ONLINE'}</span>
                      </div>
                      <div className="gallery-counter">
                        {String(currentSlide + 1).padStart(2, '0')} / {String(totalSlides).padStart(2, '0')}
                      </div>
                    </div>
                    <div className="gallery-progress" style={{ animationDuration: `${AUTOPLAY_DELAY}ms` }}></div>
                  </div>

                  <div className="gallery-controls">
                    <button className="gallery-control-btn" type="button" onClick={goPrevious} disabled={totalSlides < 2 || isTransitioning} aria-label="Previous slide">
                      <i className="fas fa-chevron-left"></i>
                    </button>
                    <button className="gallery-control-btn" type="button" onClick={goNext} disabled={totalSlides < 2 || isTransitioning} aria-label="Next slide">
                      <i className="fas fa-chevron-right"></i>
                    </button>
                  </div>
                </div>
              ) : (
                <div className="gallery-empty">
                  <i className="fas fa-shield-alt"></i>
                  <p>No images found in frontend/src/galaries</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Story Modal */}
      {showStoryModal && (
        <div className="about-modal-overlay" onClick={() => setShowStoryModal(false)}>
          <div className="about-modal story-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title-section">
                <i className="fas fa-book-open"></i>
                <div>
                  <h3>Our Story</h3>
                  <span>The Kamelot Journey</span>
                </div>
              </div>
              <button className="modal-close" onClick={() => setShowStoryModal(false)}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="modal-body">
              <div className="story-timeline">
                <div className="timeline-item">
                  <div className="timeline-marker">
                    <span>2011</span>
                  </div>
                  <div className="timeline-content">
                    <h4>The Beginning</h4>
                    <p>Kamelot Security Services was founded with a simple mission: to place people at the heart of everything we do. Starting with just a handful of dedicated security professionals in Los Angeles, we set out to revolutionize the security industry.</p>
                  </div>
                </div>
                
                <div className="timeline-item">
                  <div className="timeline-marker">
                    <span>2023</span>
                  </div>
                  <div className="timeline-content">
                    <h4>Rapid Expansion</h4>
                    <p>Within our first year, we expanded operations to 15 states, serving over 200 clients. Our commitment to excellence and employee welfare quickly set us apart from traditional security firms.</p>
                  </div>
                </div>
                
                <div className="timeline-item">
                  <div className="timeline-marker">
                    <span>2024</span>
                  </div>
                  <div className="timeline-content">
                    <h4>Nationwide Presence</h4>
                    <p>Today, Kamelot Security operates across 16 Regions with over 3,500 security personnel. We've maintained our family-owned, woman-led values while becoming one of the most trusted names in security services.</p>
                  </div>
                </div>
                
                <div className="timeline-item highlight">
                  <div className="timeline-marker">
                    <i className="fas fa-star"></i>
                  </div>
                  <div className="timeline-content">
                    <h4>Our Philosophy</h4>
                    <p>We believe that when our team members are supported, valued, and encouraged to reach their full potential, they make a real difference for our clients and the communities we serve. This philosophy has been the cornerstone of our success.</p>
                  </div>
                </div>
              </div>
              
              <div className="story-values">
                <h4><i className="fas fa-heart"></i> Core Values</h4>
                <div className="values-grid">
                  <div className="value-item">
                    <i className="fas fa-shield-alt"></i>
                    <span>Trust</span>
                  </div>
                  <div className="value-item">
                    <i className="fas fa-handshake"></i>
                    <span>Respect</span>
                  </div>
                  <div className="value-item">
                    <i className="fas fa-chart-line"></i>
                    <span>Growth</span>
                  </div>
                  <div className="value-item">
                    <i className="fas fa-award"></i>
                    <span>Excellence</span>
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="story-cta-btn" onClick={() => setShowStoryModal(false)}>
                <i className="fas fa-check"></i>
                <span>Got It</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Background Effects */}
      <div className="about-bg-effects">
        <div className="about-grid-bg"></div>
        <div className="about-glow-orb orb-1"></div>
        <div className="about-glow-orb orb-2"></div>
        <div className="about-scanlines"></div>
      </div>

      <div className="about-container">
        {/* Section Header */}
        <div className="about-header">
          <div className="about-badge">
            <i className="fas fa-info-circle"></i>
            <span>ABOUT US</span>
          </div>
          <h2 className="about-title">
            <span className="glitch-text" data-text="WHO WE ARE">WHO WE ARE</span>
          </h2>
          <p className="about-subtitle">
            Serving with purpose and growing together since 2011
          </p>
        </div>

        {/* Stats Row */}
        <div className="about-stats">
          {stats.map((stat, index) => (
            <div 
              key={index} 
              className="stat-card"
              style={{ animationDelay: `${index * 0.15}s` }}
            >
              <div className="stat-icon">
                <i className={`fas ${stat.icon}`}></i>
              </div>
              <div className="stat-value">
                <span className="stat-number">{stat.value}</span>
                <span className="stat-suffix">{stat.suffix}</span>
              </div>
              <div className="stat-label">{stat.label}</div>
              <div className="stat-glow"></div>
            </div>
          ))}
        </div>

        {/* Main Content Grid */}
        <div className="about-content-grid">
          {/* Left Column - Story */}
          <div className="about-story">
            <div className="story-card">
              <div className="story-header">
                <div className="story-icon">
                  <i className="fas fa-book-open"></i>
                </div>
                <h3>Our Story</h3>
              </div>
              
              <div className="story-content">
                <p className="story-highlight">
                  Kamelot Security Services, Inc. places people at the heart of everything we do.
                </p>
                <p>
                  Our culture emphasizes trust, respect, and personal growth, ensuring employees never feel like they are just a number. We believe that when our team members are supported, valued, and encouraged to reach their full potential, they make a real difference for our clients and the communities we serve.
                </p>
                <p>
                  As a family-owned business that is proudly woman owned and led, we bring a personal commitment to excellence that sets us apart in the industry.
                </p>
              </div>

              <div className="story-actions">
                <button className="about-btn primary" onClick={() => setShowStoryModal(true)}>
                  <i className="fas fa-book"></i>
                  <span>Read Our Story</span>
                </button>
                <button className="about-btn secondary" onClick={() => setShowGalleryModal(true)}>
                  <i className="fas fa-images"></i>
                  <span>View Gallery</span>
                </button>
              </div>
            </div>
          </div>

          {/* Right Column - Features */}
          <div className="about-features">
            <div className="features-grid">
              {features.map((feature, index) => (
                <div 
                  key={index} 
                  className="feature-card"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <div className="feature-icon">
                    <i className={`fas ${feature.icon}`}></i>
                  </div>
                  <h4>{feature.title}</h4>
                  <p>{feature.desc}</p>
                  <div className="feature-hover-glow"></div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Industries Served */}
        <div className="about-industries">
          <h3 className="industries-title">
            <i className="fas fa-industry"></i>
            Industries We Serve
          </h3>
          <div className="industries-chips">
            {industries.map((industry, index) => (
              <div 
                key={index} 
                className="industry-chip-enhanced"
                style={{ animationDelay: `${index * 0.08}s` }}
              >
                <i className="fas fa-check-circle"></i>
                <span>{industry}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom CTA */}
        <div className="about-cta">
          <div className="cta-content">
            <h3>Ready to Secure Your Future?</h3>
            <p>Join thousands of satisfied clients who trust Kamelot Security</p>
          </div>
          <button className="about-cta-btn">
            <span className="btn-content">
              <i className="fas fa-rocket"></i>
              <span>Get Started Today</span>
            </span>
            <div className="btn-shine"></div>
          </button>
        </div>
      </div>
    </section>
  );
};

export default AboutSection;
